/**
 * ErrorBoundary — top-level React error boundary.
 *
 * Catches render/lifecycle exceptions anywhere below it and renders a
 * fallback UI instead of white-screening the entire application. Includes
 * self-heal actions (reload, unregister service worker + clear caches,
 * clear localStorage) so a bad cache or malformed API payload never leaves
 * the user stranded.
 *
 * Feb 2026 R34 — added after a production incident where a workspace logo
 * upload left users on a blank /app page. The root cause was an
 * unhandled runtime error bubbling above <App /> with no fallback.
 */
import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console for debugging; do NOT rethrow.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Caught render error:", error, info);
    this.setState({ info });
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  hardReload = () => {
    try {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => { try { r.unregister(); } catch { /* ignore */ } });
        }).catch(() => { /* ignore */ });
      }
      if (typeof caches !== "undefined") {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => { /* ignore */ });
      }
    } finally {
      // Force a bypass-cache reload once the SW is gone.
      setTimeout(() => window.location.reload(), 250);
    }
  };

  wipeAndGoHome = () => {
    try { localStorage.clear(); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    this.hardReload();
    // Also redirect after the reload kicks in.
    setTimeout(() => { window.location.href = "/"; }, 300);
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { error, info } = this.state;
    const msg = (error && (error.message || String(error))) || "Unknown error";
    const stack = (info && info.componentStack) || (error && error.stack) || "";
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "100vh",
          padding: "48px 24px",
          fontFamily: "system-ui, sans-serif",
          background: "#0a0a0a",
          color: "#f5f5f5",
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{
            fontSize: 11,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "#f97316",
            marginBottom: 8,
          }}>Something went wrong</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: "0 0 12px" }}>
            The app hit an unexpected error.
          </h1>
          <p style={{ color: "#a3a3a3", lineHeight: 1.5, marginBottom: 24 }}>
            You&apos;re seeing this because a render or startup step failed. This
            is almost always fixed by clearing the cached shell and reloading.
            Nothing has been lost — your data is safe on the server.
          </p>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 32 }}>
            <button
              data-testid="error-boundary-retry"
              onClick={this.reset}
              style={btnStyle("#f97316", "#0a0a0a")}
            >Try again</button>
            <button
              data-testid="error-boundary-clear-cache"
              onClick={this.hardReload}
              style={btnStyle("#f5f5f5", "#0a0a0a")}
            >Clear cache &amp; reload</button>
            <button
              data-testid="error-boundary-wipe"
              onClick={this.wipeAndGoHome}
              style={btnStyle("transparent", "#f5f5f5", "#f5f5f5")}
            >Sign out &amp; start over</button>
          </div>

          <details style={{
            background: "#171717",
            border: "1px solid #262626",
            padding: 16,
            fontSize: 12,
            fontFamily: "ui-monospace, monospace",
          }}>
            <summary style={{ cursor: "pointer", color: "#a3a3a3" }}>
              Technical details (share with support if the issue persists)
            </summary>
            <div style={{ color: "#fbbf24", marginTop: 12, wordBreak: "break-word" }}>{msg}</div>
            {stack && (
              <pre style={{
                whiteSpace: "pre-wrap",
                color: "#737373",
                marginTop: 12,
                fontSize: 11,
                maxHeight: 260,
                overflow: "auto",
              }}>{stack}</pre>
            )}
          </details>
        </div>
      </div>
    );
  }
}

function btnStyle(bg, fg, border) {
  return {
    background: bg,
    color: fg,
    border: border ? `1px solid ${border}` : "none",
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.02em",
  };
}
